import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Brain, ShieldCheck, Clock, ExternalLink } from "lucide-react";
import api from "../lib/api";

const BAND_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
};

// Read-only, no-auth view of a MoCA-voice score. Reached via /share/moca-voice/:token
// which the respondent generates on their result page. We deliberately show a
// simplified brand header (no sign-in / language switcher) because the viewer
// may not have a Manodayam account.
export default function MocaShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use the raw axios instance so we skip the auth interceptor entirely —
    // this endpoint is public.
    api.get(`/share/moca-voice/${token}`, { headers: { Authorization: "" } })
      .then((r) => setData(r.data))
      .catch((e) => {
        const detail = e?.response?.data?.detail || "Share link is not valid.";
        setError(detail);
      });
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <header className="mano-hairline bg-background/90 sticky top-0 z-40">
          <div className="mano-container flex items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/manodayam-logo.png" alt="Manodayam" className="h-8 w-auto" />
              <span className="hidden md:inline text-xs text-muted-foreground border-l border-border/60 pl-3">
                Consent-first screening
              </span>
            </Link>
          </div>
        </header>
        <main className="mano-container-narrow py-20 text-center">
          <div className="mx-auto max-w-md">
            <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-4" />
            <h1 className="font-display text-2xl font-semibold mb-2" data-testid="share-error-title">
              This link isn't available
            </h1>
            <p className="text-sm text-muted-foreground" data-testid="share-error-detail">
              {error}
            </p>
            <p className="text-xs text-muted-foreground mt-6">
              Ask the person who shared this link to generate a fresh one from their Manodayam result page.
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <main className="mano-container-narrow py-20 text-muted-foreground">Loading shared result…</main>
      </div>
    );
  }

  const score = data.score || {};
  const totalMax = score.total_max || 30;

  return (
    <div className="min-h-screen bg-background">
      <header className="mano-hairline bg-background/90 sticky top-0 z-40">
        <div className="mano-container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="share-brand-link">
            <img src="/manodayam-logo.png" alt="Manodayam" className="h-8 w-auto" />
            <span className="hidden md:inline text-xs text-muted-foreground border-l border-border/60 pl-3">
              Consent-first screening
            </span>
          </Link>
          <a href="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
             data-testid="share-visit-link">
            Learn about Manodayam <ExternalLink size={12} />
          </a>
        </div>
      </header>

      <main className="mano-container-narrow py-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
            <Brain size={14} /> Voice-driven MoCA · Read-only shared view
          </div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight"
              data-testid="share-title">
            Shared MoCA-voice result
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            This is a read-only, MoCA-inspired voice screening summary shared with you
            by a respondent. It is a screening aid — a clinician-administered MoCA is
            still required for diagnostic purposes.
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground"
               data-testid="share-meta">
            {data.shared_at && (
              <span className="inline-flex items-center gap-1"><Clock size={11} />
                Shared {(data.shared_at || "").replace("T", " ").slice(0, 16)}
              </span>
            )}
            {data.expires_at && (
              <span>Link expires {(data.expires_at || "").slice(0, 10)}</span>
            )}
            {data.language && <span>Respondent language · {String(data.language).toUpperCase()}</span>}
          </div>
        </div>

        <Card className="mano-card mb-6">
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="font-display text-4xl leading-none" data-testid="share-total">
                  {score.total ?? "-"}<span className="text-muted-foreground text-lg"> / {totalMax}</span>
                </div>
              </div>
              <Badge className={"border text-sm px-3 py-1 " + (BAND_TONE[score.band] || "")}
                     data-testid={`share-band-${score.band}`}>
                {score.band}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">{score.label}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {(score.domain_scores || []).map((d) => (
                <div key={d.key}
                     className="flex items-center justify-between rounded-lg border border-border/60 p-3"
                     data-testid={`share-domain-${d.key}`}>
                  <div className="text-sm">{d.label}</div>
                  <div className="font-display text-base">
                    {d.score}<span className="text-muted-foreground"> / {d.max}</span>
                  </div>
                </div>
              ))}
            </div>
            {score.has_clinician_overrides && (
              <div className="text-xs text-muted-foreground italic">
                Note: this total includes one or more clinician overrides applied
                after the automated scorer ran.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8">
            <h2 className="font-display font-semibold text-lg mb-1">Per-task transcripts</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Whisper transcribed each spoken answer; Claude scored the transcript against
              a clinical rubric. Audio recordings are not included in shared links.
            </p>
            <div className="space-y-3">
              {(data.tasks || []).map((t) => {
                const rub = t.rubric || {};
                const effective = rub.override?.score ?? rub.score;
                return (
                  <div key={t.id}
                       className="rounded-lg border border-border/60 p-3"
                       data-testid={`share-task-${t.id}`}>
                    <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                      <div className="text-sm font-medium capitalize">{t.id.replace(/_/g, " ")}</div>
                      <div className="text-xs text-muted-foreground">
                        {effective ?? "-"} / {rub.max ?? "-"}
                        {rub.override && <span className="ml-1 text-[10px] uppercase text-secondary-foreground">clinician</span>}
                      </div>
                    </div>
                    {t.target_text && (
                      <div className="text-xs text-muted-foreground italic mb-1">
                        Target: “{t.target_text}”
                      </div>
                    )}
                    <div className="text-sm bg-muted/40 rounded p-2 whitespace-pre-wrap"
                         data-testid={`share-transcript-${t.id}`}>
                      {t.transcript || <span className="text-muted-foreground italic">(no transcript)</span>}
                    </div>
                    {rub.rationale && (
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">Rubric:</span> {rub.rationale}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-8">
          Manodayam · Consent-first screening. This link is read-only and can be
          revoked at any time by the respondent.
        </p>
      </main>
    </div>
  );
}
